import {ChangeDetectionStrategy, Component, computed, effect, inject, output, signal} from "@angular/core"
import {cardAnimations} from "@app/common/components/cards/animations"
import {BaseSelectComponent} from "@app/platform/components/base/base-select/base-select.component"
import {getNodeIconClass} from "@app/template-editor/helpers/template-icons"
import {Matrix4} from "@cm/math"
import {
    AreaLight,
    Camera,
    defaultsForToneMapping,
    HdriLight,
    LightPortal,
    PointLight,
    SunLight,
    TemplateNode,
    PostProcessRender,
    Render,
    SceneProperties,
} from "@cm/template-nodes"
import {CardErrorComponent} from "@common/components/cards/card-error/card-error.component"
import {CardPlaceholderComponent} from "@common/components/cards/card-placeholder/card-placeholder.component"
import {SearchComponent} from "@common/components/inputs/search/search.component"
import {InfiniteListComponent} from "@common/components/lists/infinite-list/infinite-list.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {AuthService} from "@common/services/auth/auth.service"
import {HdriFilterInput} from "@generated"
import {SelectHdriItemFragment, SelectHdriItemsGQL} from "@platform/components/hdris/select-hdri/select-hdri.generated"
import {TemplateAddCardComponent} from "@app/template-editor/components/template-add-card/template-add-card.component"

type SceneItem = {
    name: string
    node: TemplateNode
    iconClass: string
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-add-scene-item",
    templateUrl: "./template-add-scene-item.component.html",
    styleUrl: "./template-add-scene-item.component.scss",
    animations: [...cardAnimations],
    imports: [SearchComponent, InfiniteListComponent, TemplateAddCardComponent, CardPlaceholderComponent, CardErrorComponent],
})
export class TemplateAddSceneItemComponent extends BaseSelectComponent<SelectHdriItemFragment, HdriFilterInput> {
    private readonly auth = inject(AuthService)

    constructor() {
        super()

        effect(() => {
            this.$lockOrganization.set(!this.auth.$actingAsStaff())
        })
    }

    readonly selectHdriItemsGql = inject(SelectHdriItemsGQL)
    private rawData = this.implementDataLoader(fetchThrowingErrors(this.selectHdriItemsGql))
    readonly $data = computed(() => this.rawData().items.map((item) => ({...item, node: item.data ? this.getHdriLight(item.data) : undefined})))
    readonly onItemClicked = output()

    readonly $currentSection = signal<"camera" | "light" | "hdri">("camera")

    readonly $cameraSceneItems = computed<SceneItem[]>(() => {
        return [
            {
                name: "Camera",
                node: new Camera({
                    parameters: {
                        name: "Camera",
                        target: [0, 0, 0],
                        targeted: true,
                        lockedTransform: Matrix4.translation(0, 0, 100).toArray(),
                        visible: true,
                        resolutionX: 1920,
                        resolutionY: 1080,
                        filmGauge: 36,
                        fStop: 128,
                        focalLength: 50,
                        focalDistance: 100,
                        autoFocus: true,
                        ev: 0.0,
                        shiftX: 0,
                        shiftY: 0,
                        automaticVerticalTilt: false,
                        enableOrbiting: true,
                        enablePanning: true,
                        screenSpacePanning: true,
                        adaptAspectRatio: true,
                        toneMapping: defaultsForToneMapping("pbr-neutral"),
                        enableDamping: false,
                        dampingFactor: 0.05,
                    },
                }),
                iconClass: getNodeIconClass(Camera.getNodeClass()),
            },
            {
                name: "Render",
                node: new Render({
                    parameters: {
                        percentage: 100,
                        samples: 100,
                        renderTiling: "auto",
                        denoiseTiling: "auto",
                    },
                }),
                iconClass: getNodeIconClass(Render.getNodeClass()),
            },
            {
                name: "Post Process Render",
                node: new PostProcessRender({
                    parameters: {
                        mode: "whiteBackground",
                        ev: 0,
                        whiteBalance: 0,
                        transparent: false,
                        composite: false,
                        backgroundColor: [1, 1, 1],
                        processShadows: false,
                        shadowInner: 0,
                        shadowOuter: 0,
                        shadowFalloff: 1,
                        shadowOpacity: 1,
                        autoCrop: false,
                        autoCropMargin: 50,
                        toneMapping: defaultsForToneMapping("pbr-neutral"),
                    },
                }),
                iconClass: getNodeIconClass(PostProcessRender.getNodeClass()),
            },
            {
                name: "Scene Properties",
                node: new SceneProperties({
                    parameters: {
                        maxSubdivisionLevel: 2,
                        backgroundColor: [1, 1, 1],
                        shiftAndZoomBackgroundImage: false,
                        uiColor: [0, 0, 0],
                        uiStyle: "icons",
                        iconSize: 24,
                        enableAr: false,
                        enableSalesEnquiry: false,
                        textureFiltering: false,
                        enableRealtimeShadows: true,
                        enableRealtimeLights: true,
                        enableRealtimeMaterials: true,
                        enableOnboardingHint: false,
                        enableGltfDownload: false,
                        enableStlDownload: false,
                        enableSnapshot: true,
                        enablePdfDownload: false,
                        enableFullscreen: true,
                        environmentMapMode: "full",
                        showAnnotations: true,
                        enableDimensionGuides: false,
                        enableAdaptiveSubdivision: false,
                        verticalArPlacement: false,
                    },
                }),
                iconClass: getNodeIconClass(SceneProperties.getNodeClass()),
            },
        ]
    })

    readonly $lightSceneItems = computed<SceneItem[]>(() => {
        return [
            {
                name: "Area Light",
                node: new AreaLight({
                    parameters: {
                        name: "Area Light",
                        width: 100,
                        height: 100,
                        lockedTransform: Matrix4.identity().toArray(),
                        visible: true,
                        target: [0, 0, -100],
                        targeted: true,
                        intensity: 1,
                        color: [1, 1, 1],
                        on: true,
                        directionality: 0,
                        visibleDirectly: true,
                        visibleInReflections: true,
                        visibleInRefractions: true,
                        transparent: false,
                        lightType: "Corona",
                    },
                }),
                iconClass: getNodeIconClass(AreaLight.getNodeClass()),
            },
            {
                name: "Point Light",
                node: new PointLight({
                    parameters: {
                        name: "Point Light",
                        radius: 5,
                        lockedTransform: Matrix4.identity().toArray(),
                        visible: true,
                        intensity: 30,
                        color: [1, 1, 1],
                        on: true,
                        visibleDirectly: true,
                        visibleInReflections: true,
                        visibleInRefractions: true,
                        lightType: "Corona",
                    },
                }),
                iconClass: getNodeIconClass(PointLight.getNodeClass()),
            },
            {
                name: "Sun Light",
                node: new SunLight({
                    parameters: {
                        name: "Sun Light",
                        angle: 10,
                        lockedTransform: Matrix4.translation(-100, 230, 170).multiply(Matrix4.rotationY(-30)).multiply(Matrix4.rotationX(-50)).toArray(),
                        visible: true,
                        intensity: 0.5,
                        color: [1, 1, 1],
                        on: true,
                        visibleDirectly: true,
                        visibleInReflections: true,
                        visibleInRefractions: true,
                        lightType: "Blender",
                    },
                }),
                iconClass: getNodeIconClass(SunLight.getNodeClass()),
            },
            {
                name: "Light Portal",
                node: new LightPortal({
                    parameters: {
                        name: "Light Portal",
                        width: 100,
                        height: 100,
                        lockedTransform: Matrix4.identity().toArray(),
                        visible: true,
                    },
                }),
                iconClass: getNodeIconClass(LightPortal.getNodeClass()),
            },
        ]
    })

    readonly $sceneItems = computed<SceneItem[] | undefined>(() => {
        const currentSection = this.$currentSection()
        if (currentSection === "camera") return this.$cameraSceneItems()
        else if (currentSection === "light") return this.$lightSceneItems()
        else return undefined
    })

    private getHdriLight(data: SelectHdriItemFragment) {
        const {name, legacyId} = data
        return new HdriLight({
            parameters: {
                name: name ?? "HDRI Light",
                hdriId: legacyId,
                intensity: 1,
                saturation: 100,
                rotation: [0, 0, 0],
                mirror: false,
            },
        })
    }
}
